import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../common/Card';
import Button from '../common/Button';
import Table from '../common/Table';
import api from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/formatters';
import '../../styles/admin.css';

const AdminDashboard = () => {
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    totalBusinesses:     0,
    activeSubscriptions: 0,
    trialBusinesses:     0,
    suspendedBusinesses: 0,
    totalUsers:          0,
    monthlyRevenue:      0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/admin/stats');
      const data = response.data;

      const businesses     = data.businesses     || {};
      const users          = data.users          || {};
      const monthlyRevenue = data.monthly_revenue || [];

      const currentMonth     = new Date().toISOString().slice(0, 7);
      const currentMonthData = monthlyRevenue.find(r => r.month === currentMonth);

      setStats({
        totalBusinesses:     parseInt(businesses.total)     || 0,
        activeSubscriptions: parseInt(businesses.active)    || 0,
        trialBusinesses:     parseInt(businesses.trial)     || 0,
        suspendedBusinesses: parseInt(businesses.suspended) || 0,
        totalUsers:          parseInt(users.total)          || 0,
        monthlyRevenue:      parseFloat(currentMonthData?.revenue || 0)
      });

      setRecentActivity(data.recent_activity || []);

    } catch (error) {
      console.error('Failed to load admin data:', error);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const activityColumns = [
    {
      key: 'created_at',
      label: 'Time',
      render: (value) => formatDate(value)
    },
    {
      key: 'user',
      label: 'User',
      render: (_, row) =>
        `${row.first_name || ''} ${row.last_name || ''}`.trim() || row.username || 'System'
    },
    {
      key: 'action',
      label: 'Action',
      render: (value) => (
        <span className={`action-badge ${value?.split('_')[0]}`}>
          {value}
        </span>
      )
    },
    {
      key: 'business_name',
      label: 'Business',
      render: (value) => value || 'System'
    }
  ];

  if (loading) return <div className="loading">Loading dashboard...</div>;

  if (error) {
    return (
      <div className="error-state">
        <p>{error}</p>
        <Button variant="primary" onClick={loadAdminData}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h1>Admin Dashboard</h1>
        <div className="header-actions">
          <Button variant="primary" onClick={loadAdminData}>
            Refresh Data
          </Button>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card primary">
          <div className="stat-icon">🏢</div>
          <div className="stat-content">
            <span className="stat-label">Total Businesses</span>
            <span className="stat-value">{stats.totalBusinesses}</span>
          </div>
        </div>

        <div className="stat-card success">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <span className="stat-label">Active Subscriptions</span>
            <span className="stat-value">{stats.activeSubscriptions}</span>
          </div>
        </div>

        <div className="stat-card warning">
          <div className="stat-icon">⏳</div>
          <div className="stat-content">
            <span className="stat-label">Trial Businesses</span>
            <span className="stat-value">{stats.trialBusinesses}</span>
          </div>
        </div>

        <div className="stat-card danger">
          <div className="stat-icon">⚠️</div>
          <div className="stat-content">
            <span className="stat-label">Suspended</span>
            <span className="stat-value">{stats.suspendedBusinesses}</span>
          </div>
        </div>

        <div className="stat-card info">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <span className="stat-label">Total Users</span>
            <span className="stat-value">{stats.totalUsers}</span>
          </div>
        </div>

        <div className="stat-card purple">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <span className="stat-label">Monthly Revenue</span>
            <span className="stat-value">{formatCurrency(stats.monthlyRevenue)}</span>
          </div>
        </div>
      </div>

      <div className="dashboard-grid">
        <Card title="Quick Actions" className="quick-actions-card">
          <div className="quick-actions-grid">
            <button
              className="quick-action-btn"
              onClick={() => navigate('/admin/modules')}
            >
              <span className="action-icon">🧩</span>
              <span>App Modules</span>
            </button>
            <button
              className="quick-action-btn"
              onClick={() => navigate('/admin/mpesa')}
            >
              <span className="action-icon">📲</span>
              <span>M-Pesa Billing</span>
            </button>
            <button
              className="quick-action-btn"
              onClick={() => navigate('/admin/homepage')}
            >
              <span className="action-icon">🏠</span>
              <span>Homepage</span>
            </button>
            <button
              className="quick-action-btn"
              onClick={() => navigate('/admin/businesses')}
            >
              <span className="action-icon">🏢</span>
              <span>Businesses</span>
            </button>
            <button
              className="quick-action-btn"
              onClick={() => navigate('/admin/plans')}
            >
              <span className="action-icon">📋</span>
              <span>Plans</span>
            </button>
            <button
              className="quick-action-btn"
              onClick={() => navigate('/admin/users')}
            >
              <span className="action-icon">👤</span>
              <span>Users</span>
            </button>
            <button
              className="quick-action-btn"
              onClick={() => navigate('/admin/audit')}
            >
              <span className="action-icon">📜</span>
              <span>Audit Logs</span>
            </button>
          </div>
        </Card>

        <Card title="System Health" className="system-health-card">
          <div className="health-indicators">
            <div className="health-item">
              <span>Database:</span>
              <span className="status healthy">✅ Connected</span>
            </div>
            <div className="health-item">
              <span>API Status:</span>
              <span className="status healthy">✅ Online</span>
            </div>
            <div className="health-item">
              <span>Total Revenue:</span>
              <span className="status">{formatCurrency(stats.monthlyRevenue)}</span>
            </div>
            <div className="health-item">
              <span>Active Ratio:</span>
              <span className="status healthy">
                {stats.totalBusinesses > 0
                  ? Math.round((stats.activeSubscriptions / stats.totalBusinesses) * 100)
                  : 0}% active
              </span>
            </div>
          </div>
        </Card>
      </div>

      <Card title="Recent Activity" className="recent-activity-card">
        <Table
          columns={activityColumns}
          data={recentActivity.slice(0, 10)}
          emptyMessage="No recent activity found."
        />
        <div className="card-footer">
          <Button
            variant="secondary"
            onClick={() => navigate('/admin/audit')}
          >
            View All Activity →
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default AdminDashboard;